import { ChevronRight, Link2 } from "lucide-react";
import { useId } from "react";

import {
  GOAL_TIMELINE_OPTIONS,
  GoalStatusBadge,
} from "~/components/ui/goal-badges";
import { Progress } from "~/components/ui/progress";
import { useDecryptedResource } from "~/lib/crypto";
import type { GetKeyGrantFn, KeyManager } from "~/lib/crypto";

import type { GoalItem } from "./goal-groups";

/** "2 of 5 steps", or "No steps yet". */
function stepCountLabel({ total, done }: GoalItem["steps"]): string {
  if (total === 0) return "No steps yet";
  return `${done} of ${total} ${total === 1 ? "step" : "steps"}`;
}

/**
 * One goal in a list: its title, timeframe, step count and state. The client
 * and their coach see the same row; only the client sees the public link mark.
 */
export function GoalRow({
  item,
  onOpen,
  keyManager,
  getKeyGrant,
  showPublicLink = false,
}: {
  item: GoalItem;
  onOpen: (item: GoalItem) => void;
  keyManager: KeyManager | null;
  getKeyGrant?: GetKeyGrantFn;
  showPublicLink?: boolean;
}) {
  const { goal, steps } = item;
  const { data: decrypted, isDecrypting } = useDecryptedResource({
    resourceType: "goal",
    resourceId: goal.id,
    keyManager,
    getKeyGrant,
    fields: { title: { encrypted: goal.encryptedTitle } },
  });
  const timeline = GOAL_TIMELINE_OPTIONS.find(
    (option) => option.id === goal.timeline,
  )?.label;
  const titleId = useId();
  const detailsId = useId();
  const statusId = useId();

  return (
    <div className="relative flex items-center gap-4 px-4 py-3 transition-colors hover:bg-gray-50 has-[button:focus-visible]:bg-gray-50">
      {/*
        The row's button holds no text: the Meta pixel (`__root.tsx`) reports
        the text of every button clicked, and the title is decrypted text. The
        button takes its name from the title by reference and covers the row.
      */}
      <button
        type="button"
        aria-labelledby={titleId}
        aria-describedby={`${detailsId} ${statusId}`}
        onClick={() => onOpen(item)}
        className="absolute inset-0 z-10 cursor-pointer focus-visible:outline-none"
      />
      <div className="min-w-0 flex-1 space-y-1">
        <p
          id={titleId}
          className="text-base font-medium break-words text-gray-900"
        >
          {isDecrypting ? (
            <span className="inline-block h-5 w-40 animate-pulse rounded bg-gray-100" />
          ) : (
            (decrypted.title ?? "Untitled")
          )}
        </p>
        <div
          id={detailsId}
          className="text-text-tertiary flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
        >
          <span>
            {[timeline, stepCountLabel(steps)].filter(Boolean).join(" · ")}
          </span>
          {steps.total > 0 && (
            <Progress
              value={(steps.done / steps.total) * 100}
              aria-hidden
              className="h-1 w-16 bg-gray-100"
            />
          )}
          {showPublicLink && goal.shareSlug && (
            <span className="flex items-center gap-1">
              <Link2 className="size-4" aria-hidden />
              Public link
            </span>
          )}
        </div>
      </div>
      <span id={statusId}>
        <GoalStatusBadge status={goal.status} />
      </span>
      <ChevronRight className="size-4 shrink-0 text-gray-400" aria-hidden />
    </div>
  );
}
